import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AttributePill, ActionChip } from './pills';
import { ValueSheet, ValueSheetOption } from './ValueSheet';
import { ConfirmDialog } from './ConfirmDialog';
import { SheetShell } from './SheetShell';
import { useListFetch } from './useListFetch';
import { useWriteFeedback } from './useWriteFeedback';
import { LastActivity } from './DealRows';

export interface DealDetail {
  id: string;
  name: string;
  value: number | null;
  stageId: string | null;
  stageName: string | null;
  stageEnteredAt: string | null;
  ownerId: string | null;
  ownerName: string | null;
  company: { id: string; name: string } | null;
  lead: { id: string; name: string | null } | null;
  lastCustomerActivityAt: string | null;
  staleAfterDays: number | null;
  status: string | null;
  closedAt: string | null;
  lostReason: string | null;
  tasks: Array<{ id: string; title: string; status: string; completedAt: string | null }>;
  recentActivity: Array<{ id: string; type: string; summary: string; occurredAt: string }>;
}

// #50 (cmdr/DevOps, 2026-08-23): same converged fix as the other 3 panels — `status`/`closedAt` empty
// to `null` (never a guessed open/closed state), so the Close/Reopen action pair below (gated on
// `deal.status`) always reflects the shell's honest "don't know yet" during a revalidate, never a
// stale won/lost verdict carried over from the previous deal.
function emptyDeal(id: string): DealDetail {
  return { id, name: '', value: null, stageId: null, stageName: null, stageEnteredAt: null, ownerId: null, ownerName: null, company: null, lead: null, lastCustomerActivityAt: null, staleAfterDays: null, status: null, closedAt: null, lostReason: null, tasks: [], recentActivity: [] };
}

/**
 * DealDetailPanel — #62 Pipeline depth spec §2, applying the Tasks-loop shape (cmdr, 2026-08-23). ONE
 * surface for create AND detail/edit. CREATE requires name + EXACTLY ONE LINK (company OR contact) —
 * settled with UXeng/PM: deals originate from conversations, so requiring a company breaks the path
 * where a WhatsApp lead becomes a deal before anyone knows the company.
 *
 * Close won/lost is the pipeline's highest-value verb (§2) — an ActionChip pair, reversible via Reopen
 * (same "no dead-end destructive verb" shape as Task's Complete/Undo). Delete never deletes linked
 * tasks — they're re-parented to the company or orphaned, disclosed in the confirm.
 */
export const DealDetailPanel: React.FC<{
  dealId: string | 'new';
  sheetShape: 'sheet' | 'popover';
  onClose: () => void;
  onCreated: (deal: DealDetail) => void;
  onUpdated: (deal: DealDetail) => void;
  /** survivors — the D-D7 disclosure: what was KEPT after the delete, so a detach never reads as a
   *  cascade. `companyName` is where the kept tasks went (null = orphaned, no company to fall to). */
  onDeleted: (dealId: string, survivors: { reparentedTasks: number; companyName: string | null }) => void;
}> = ({ dealId, sheetShape, onClose, onCreated, onUpdated, onDeleted }) => {
  const { t } = useTranslation('common');
  const [deal, setDeal] = useState<DealDetail | null>(null);
  const [loadState, setLoadState] = useState<'loading' | 'ready' | 'error'>(dealId === 'new' ? 'ready' : 'loading');
  const [createName, setCreateName] = useState('');
  // A3 (design §4.3) — write failures visible; landed-but-unconfirmed is a third outcome (see the
  // TicketDetailPanel twin for the full rationale).
  const feedback = useWriteFeedback();
  const [createError, setCreateError] = useState(false);
  const [pendingCreatedId, setPendingCreatedId] = useState<string | null>(null);
  const [createValue, setCreateValue] = useState('');
  const [linkKind, setLinkKind] = useState<'company' | 'lead'>('company');
  const [linkId, setLinkId] = useState<string | null>(null);
  const [companies, setCompanies] = useState<{ id: string; name: string }[]>([]);
  const [contacts, setContacts] = useState<{ id: string; name: string | null }[]>([]);
  const [members, setMembers] = useState<{ id: string; name: string | null }[]>([]);
  const [editingField, setEditingField] = useState<'name' | 'value' | null>(null);
  const [fieldDraft, setFieldDraft] = useState('');
  const [editingPicker, setEditingPicker] = useState<{ field: 'stage' | 'owner'; anchor: HTMLElement } | null>(null);
  const [showLostReason, setShowLostReason] = useState(false);
  const [lostReasonDraft, setLostReasonDraft] = useState('');
  const hasLoadedRef = useRef(false);

  // #50 (cmdr/DevOps, 2026-08-23): converged fix — on a real dealId CHANGE (not the true first mount),
  // reset synchronously to an honest empty shell, never stale-and-scrimmed, never null. `deal` stays
  // truthy the whole revalidating window (no detach — invariant 1); every field inside reads as
  // genuinely absent (no wrong-entity-flash — invariant 2). A revalidation error does not null `deal`.
  useEffect(() => {
    if (dealId === 'new') return;
    // 🔴 root fix (cmdr/DevOps, 2026-08-25 — same defect found+fixed in InvoiceDetailPanel, applied
    // here on the identical pattern): right after create, the parent flips dealId from 'new' to the
    // real id on the SAME panel instance — this effect re-fires, but `deal` here already holds fresh
    // data for that exact id. Re-fetching anyway forces a real `loadState:'loading'` window, and every
    // inline-edit click handler gates on `!isLoading` — a click landing in that window is SILENTLY
    // SWALLOWED. Skip the redundant round-trip when we already have this exact record.
    if (deal && deal.id === dealId) { hasLoadedRef.current = true; return; }
    setLoadState('loading');
    if (hasLoadedRef.current) setDeal(emptyDeal(dealId));
    fetch(`/api/deals/${dealId}`).then((r) => r.ok ? r.json() : Promise.reject(r.status)).then((d: DealDetail) => {
      setDeal(d); setLoadState('ready'); hasLoadedRef.current = true;
    }).catch(() => { setLoadState('error'); });
  }, [dealId]);

  useEffect(() => {
    fetch('/api/companies').then((r) => r.ok ? r.json() : []).then(setCompanies).catch(() => {});
    fetch('/api/customers?kind=contact').then((r) => r.ok ? r.json() : []).then((rows) => setContacts((rows as any[]).map((c) => ({ id: c.id, name: c.name })))).catch(() => {});
    fetch('/api/tasks/assignees').then((r) => r.ok ? r.json() : []).then(setMembers).catch(() => {});
  }, []);

  // D-D3 — the stage vocabulary has its own fetch state: a failed /api/stages must render as a
  // picker-level error with retry, never as an empty option list the operator reads as "there are
  // no stages" (they could unset a stage and not set one, with nothing saying anything failed).
  const stagesFetch = useListFetch<{ id: string; name: string }[]>('/api/stages');
  const stages = stagesFetch.data ?? [];

  const submitCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    const name = createName.trim();
    if (!name || !linkId) return;
    const body: Record<string, unknown> = { name, value: createValue ? Number(createValue) : null };
    if (linkKind === 'company') body.companyId = linkId; else body.leadId = linkId;
    const res = await fetch('/api/deals', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }).catch(() => null);
    // D-D6: a failed create is a SAVE error — form and typed values stay; distinct message.
    if (!res?.ok) { setCreateError(true); return; }
    setCreateError(false);
    const { dealId: newId } = await res.json();
    const ok = await confirmCreated(newId);
    if (!ok) setPendingCreatedId(newId); // landed; never re-offer the form — re-check instead
  };

  const confirmCreated = async (newId: string): Promise<boolean> => {
    const detailRes = await fetch(`/api/deals/${newId}`).catch(() => null);
    if (!detailRes?.ok) return false;
    const detail: DealDetail = await detailRes.json();
    setPendingCreatedId(null);
    setDeal(detail);
    onCreated(detail);
    return true;
  };

  const patch = async (data: Partial<{ name: string; value: number | null; ownerId: string | null }>): Promise<boolean> => {
    if (!deal) return false;
    const res = await fetch(`/api/deals/${deal.id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) }).catch(() => null);
    if (!res?.ok) { feedback.fail(t('writeStates.saveFailed')); return false; }
    const updated: DealDetail = await res.json();
    setDeal(updated);
    onUpdated(updated);
    return true;
  };

  /** Confirming refetch — returns false instead of throwing (the raw await-await threw unhandled). */
  const refresh = async (): Promise<boolean> => {
    if (!deal) return false;
    const r = await fetch(`/api/deals/${deal.id}`).catch(() => null);
    if (!r?.ok) return false;
    const refreshed: DealDetail = await r.json();
    setDeal(refreshed);
    onUpdated(refreshed);
    return true;
  };
  /** D-D2 — close/reopen/applyStage had NO failure handling: a failed close re-rendered the deal
   *  unchanged and read as a mis-tap, on the verb that books revenue. Three outcomes, separated. */
  const runVerb = async (write: () => Promise<Response>): Promise<boolean> => {
    const res = await write().catch(() => null);
    if (!res?.ok) { feedback.fail(t('writeStates.actionFailed')); return false; }
    if (await refresh()) { feedback.clear(); return true; }
    feedback.unconfirmed(t('writeStates.confirmFailed'), t('writeStates.recheck'), () => { void refresh().then((ok) => { if (ok) feedback.clear(); }); });
    return true;
  };

  const applyStage = async (stageId: string) => {
    if (!deal) return;
    await runVerb(() => fetch(`/api/deals/${deal.id}/stage`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ stageId }) }));
  };

  /** Close does NOT ride runVerb: its success surface is the D-D1 disclosure, not silence. The pin
   *  (C10 — revenue stays with the company the deal was closed at) is a consequence the operator
   *  cannot see and will not predict, so the toast NAMES it; and because DN-1 rules that reopen
   *  un-pins, close stays cleanly reversible and the toast carries a working Undo (= reopen). A
   *  contact-root close pins to nothing and the copy says only the outcome. */
  const close = async (outcome: 'won' | 'lost', lostReason?: string) => {
    if (!deal) return;
    const res = await fetch(`/api/deals/${deal.id}/close`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ outcome, lostReason }) }).catch(() => null);
    if (!res?.ok) { feedback.fail(t('writeStates.actionFailed')); return; } // a refused close keeps the reason input — nothing happened
    const body: { pinnedCompany?: { name: string } | null } = await res.json().catch(() => ({}));
    setShowLostReason(false);
    const company = body.pinnedCompany?.name ?? null;
    const disclosure = company ? t(`pipelineGrid.closedPinned.${outcome}`, { company }) : t(`pipelineGrid.closedNoPin.${outcome}`);
    if (await refresh()) { feedback.undoable(disclosure, () => { void reopen(); }); return; }
    feedback.unconfirmed(t('writeStates.confirmFailed'), t('writeStates.recheck'), () => { void refresh().then((ok) => { if (ok) feedback.clear(); }); });
  };

  const reopen = async () => {
    if (!deal) return;
    await runVerb(() => fetch(`/api/deals/${deal.id}/reopen`, { method: 'POST' }));
  };

  // ConfirmDialog, never window.confirm (P7 / D-D8) — message and buttons both through t().
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const handleDelete = async () => {
    if (!deal) return;
    setConfirmingDelete(false);
    const res = await fetch(`/api/deals/${deal.id}`, { method: 'DELETE' });
    if (!res.ok) { feedback.fail(t('writeStates.actionFailed')); return; }
    const body = await res.json().catch(() => ({}));
    onDeleted(deal.id, { reparentedTasks: body.reparentedTasks ?? 0, companyName: deal.company?.name ?? null });
    onClose();
  };

  const stageOptions: ValueSheetOption[] = stages.map((s) => ({ value: s.id, label: s.name }));
  const memberOptions: ValueSheetOption[] = members.map((m) => ({ value: m.id, label: m.name ?? '—' }));
  const linkOptions = linkKind === 'company' ? companies.map((c) => ({ value: c.id, label: c.name })) : contacts.map((c) => ({ value: c.id, label: c.name ?? '—' }));

  return (
    <div className="flex flex-col h-full w-full overflow-y-auto bg-canvas" data-testid="deal-detail-panel">
      <button data-testid="deal-detail-back" className="lg:hidden self-start flex items-center gap-1 px-4 py-3 min-h-[44px] text-sm text-primary font-semibold"
        onClick={onClose}>&larr; {t('taskBoard.back')}</button>

      <div className="flex flex-col gap-4 p-4 pb-16 lg:pb-4">
        {dealId === 'new' && !deal && !pendingCreatedId && (
          <form data-testid="deal-create-form" onSubmit={submitCreate} className="rounded-xl border border-border-neutral bg-surface p-4 flex flex-col gap-3">
            <input autoFocus data-testid="deal-create-name" value={createName} onChange={(e) => setCreateName(e.target.value)}
              placeholder={t('pipelineGrid.columns.deal')} className="h-10 px-3 rounded-lg border border-border-neutral text-sm font-semibold" dir="auto" />
            <input type="number" data-testid="deal-create-value" value={createValue} onChange={(e) => setCreateValue(e.target.value)}
              placeholder={t('pipelineGrid.columns.value')} className="h-10 px-3 rounded-lg border border-border-neutral text-sm w-fit" />
            <div className="flex gap-2">
              <button type="button" data-testid="deal-link-kind-company" onClick={() => { setLinkKind('company'); setLinkId(null); }}
                className={`h-9 px-3 rounded-lg text-xs font-semibold ${linkKind === 'company' ? 'bg-primary text-white' : 'bg-canvas text-text-muted'}`}>{t('accountsGrid.columns.name')}</button>
              <button type="button" data-testid="deal-link-kind-contact" onClick={() => { setLinkKind('lead'); setLinkId(null); }}
                className={`h-9 px-3 rounded-lg text-xs font-semibold ${linkKind === 'lead' ? 'bg-primary text-white' : 'bg-canvas text-text-muted'}`}>{t('contactsGrid.columns.name')}</button>
            </div>
            <select data-testid="deal-create-link" value={linkId ?? ''} onChange={(e) => setLinkId(e.target.value || null)}
              className="h-10 px-3 rounded-lg border border-border-neutral text-sm">
              <option value="">{t('pipelineGrid.selectLink')}</option>
              {linkOptions.map((o) => <option key={o.value ?? ''} value={o.value ?? ''}>{o.label}</option>)}
            </select>
            {createError && <div className="text-xs text-opt-rose" data-testid="deal-create-error">{t('writeStates.createFailed')}</div>}
            <button type="submit" disabled={!createName.trim() || !linkId} data-testid="deal-create-submit"
              className="h-10 px-4 rounded-lg bg-primary text-white text-sm font-semibold disabled:opacity-50 self-start">
              {t('pipelineGrid.create')}
            </button>
          </form>
        )}

        {pendingCreatedId && (
          <div className="p-6 text-center text-sm text-text-muted flex flex-col items-center gap-3" data-testid="deal-create-unconfirmed">
            {t('writeStates.confirmFailed')}
            <button type="button" data-testid="deal-create-unconfirmed-retry" onClick={() => void confirmCreated(pendingCreatedId)}
              className="min-h-[44px] px-4 rounded-lg border border-border-neutral text-sm font-semibold text-text-strong hover:bg-canvas">
              {t('writeStates.recheck')}
            </button>
          </div>
        )}

        {dealId !== 'new' && loadState === 'loading' && !deal && <div className="p-6 text-center text-sm text-text-muted">…</div>}
        {loadState === 'error' && !deal && <div className="p-6 text-center text-sm text-text-muted" data-testid="deal-detail-error">{t('customers.detail.loadError')}</div>}

        {deal && (
          <div data-testid="deal-detail-content" aria-busy={loadState === 'loading'}>
            {loadState === 'loading' && (
              <div data-testid="deal-detail-revalidating" className="text-xs text-text-muted text-center py-1" aria-live="polite">{t('customers.detail.loading')}</div>
            )}
            <div className="rounded-xl border border-border-neutral bg-surface p-4">
              {editingField === 'name' ? (
                <input autoFocus data-testid="deal-name-input" value={fieldDraft} onChange={(e) => setFieldDraft(e.target.value)}
                  onBlur={() => { setEditingField(null); if (fieldDraft.trim() && fieldDraft !== deal.name) void patch({ name: fieldDraft.trim() }).then((ok) => { if (!ok) setEditingField('name'); }); }}
                  onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); if (e.key === 'Escape') setEditingField(null); }}
                  className="w-full h-9 px-2 -mx-2 rounded-lg border border-primary text-lg font-bold" dir="auto" />
              ) : (
                <h2 data-testid="deal-name" className={`text-lg font-bold text-text-strong truncate cursor-text ${deal.status ? 'opacity-70' : ''}`}
                  onClick={() => { if (loadState !== 'loading') { setFieldDraft(deal.name); setEditingField('name'); } }}>{deal.name || <span className="text-text-muted/70">—</span>}</h2>
              )}
              {/* H-7 (DN-10) — a LOST deal is a recorded FACT, not an alarm. Rose is already spoken
                  for: it marks the conversations pane's final hour (whatsapp_grid_ui asserts
                  text-opt-rose on wa-window-chip) and destructive actions. Dressing a settled outcome
                  in the danger colour makes the pipeline read as a list of problems, and it is an
                  alarm nobody can act on — the deal is closed. Won keeps emerald: that is the outcome
                  people scan for. */}
              {deal.status && (
                <div data-testid="deal-status-badge" className={`mt-1 text-xs font-semibold ${deal.status === 'won' ? 'text-opt-emerald' : 'text-text-muted'}`}>
                  {t(`pipelineGrid.status.${deal.status}`)}{deal.lostReason ? ` — ${deal.lostReason}` : ''}
                </div>
              )}
            </div>

            <div className="rounded-xl border border-border-neutral bg-surface p-4 flex flex-col gap-3">
              <div className="flex items-center gap-2 flex-wrap">
                <AttributePill value={deal.stageName} label={t('pipelineGrid.columns.stage')}
                  emptyText={t('customers.detail.setField', { field: t('pipelineGrid.columns.stage') })}
                  onOpen={(a) => { if (loadState !== 'loading') setEditingPicker({ field: 'stage', anchor: a }); }} />
                <AttributePill value={deal.ownerName} label={t('pipelineGrid.columns.owner')}
                  emptyText={t('customers.detail.setField', { field: t('pipelineGrid.columns.owner') })}
                  onOpen={(a) => { if (loadState !== 'loading') setEditingPicker({ field: 'owner', anchor: a }); }} />
              </div>
              <div className="text-xs text-text-muted">
                {t('pipelineGrid.columns.lastStageChange')}:{' '}
                {deal.status ? (
                  <span data-testid="deal-last-activity" className="text-text-muted">{t('pipelineGrid.suppressedClosed')}</span>
                ) : (
                  <LastActivity iso={deal.lastCustomerActivityAt} staleAfterDays={deal.staleAfterDays} />
                )}
              </div>
              {editingField === 'value' ? (
                <input autoFocus type="number" data-testid="deal-value-input" value={fieldDraft} onChange={(e) => setFieldDraft(e.target.value)}
                  onBlur={() => { setEditingField(null); const v = fieldDraft ? Number(fieldDraft) : null; if (v !== deal.value) void patch({ value: v }).then((ok) => { if (!ok) setEditingField('value'); }); }}
                  onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); if (e.key === 'Escape') setEditingField(null); }}
                  className="h-9 px-2 rounded-lg border border-primary text-sm w-fit" />
              ) : (
                <div data-testid="deal-value" className="text-sm text-text-strong cursor-text" onClick={() => { if (loadState !== 'loading') { setFieldDraft(deal.value != null ? String(deal.value) : ''); setEditingField('value'); } }}>
                  {t('pipelineGrid.columns.value')}: {deal.value == null ? '—' : deal.value.toLocaleString()}
                </div>
              )}
              <div className="text-sm text-text-strong">
                {t('accountsGrid.columns.name')}/{t('contactsGrid.columns.name')}: {deal.company?.name ?? deal.lead?.name ?? '—'}
              </div>

              {!deal.status ? (
                <div className="flex items-center gap-2">
                  <ActionChip testId="deal-close-won" label={t('pipelineGrid.closeWon')} onAct={() => close('won')} disabled={loadState === 'loading'} />
                  <ActionChip testId="deal-close-lost" label={t('pipelineGrid.closeLost')} onAct={() => setShowLostReason(true)} disabled={loadState === 'loading'} />
                </div>
              ) : (
                <ActionChip testId="deal-reopen" label={t('pipelineGrid.reopen')} onAct={reopen} disabled={loadState === 'loading'} />
              )}
              {showLostReason && (
                <div className="flex items-center gap-2">
                  <input autoFocus data-testid="deal-lost-reason-input" value={lostReasonDraft} onChange={(e) => setLostReasonDraft(e.target.value)}
                    placeholder={t('pipelineGrid.lostReasonPlaceholder')} className="h-9 px-2 rounded-lg border border-border-neutral text-sm flex-1" dir="auto" />
                  <button type="button" data-testid="deal-lost-confirm" disabled={!lostReasonDraft.trim()} onClick={() => close('lost', lostReasonDraft)}
                    className="h-9 px-3 rounded-lg bg-primary text-white text-sm font-semibold disabled:opacity-50">{t('pipelineGrid.confirm')}</button>
                </div>
              )}
            </div>

            <div className="rounded-xl border border-border-neutral bg-surface p-4">
              <div className="text-[11px] font-semibold text-text-muted uppercase tracking-wide mb-2">{t('customers.detail.tasks')}</div>
              {deal.tasks.length > 0 ? (
                <div className="space-y-1.5" data-testid="deal-tasks-list">
                  {deal.tasks.map((tk) => <div key={tk.id} className={`text-sm truncate ${tk.completedAt ? 'line-through text-text-muted' : 'text-text-strong'}`}>{tk.title}</div>)}
                </div>
              ) : <span className="text-text-muted italic text-sm">{t('customers.detail.noTasks')}</span>}
            </div>

            <div className="rounded-xl border border-border-neutral bg-surface p-4">
              <div className="text-[11px] font-semibold text-text-muted uppercase tracking-wide mb-2">{t('customers.detail.recordHistory')}</div>
              {deal.recentActivity.length > 0 ? (
                <div className="space-y-2" data-testid="deal-timeline-list">
                  {deal.recentActivity.map((a) => <div key={a.id} className="text-sm text-text-strong">{a.summary}</div>)}
                </div>
              ) : <span className="text-text-muted italic text-sm">{t('customers.detail.noChangesYet')}</span>}
            </div>

            <div className="pt-2 mt-2 border-t border-border-neutral">
              <button type="button" data-testid="deal-delete" onClick={() => setConfirmingDelete(true)} disabled={loadState === 'loading'}
                className="text-sm font-semibold min-h-[44px] px-1 text-opt-rose disabled:opacity-50">
                {t('pipelineGrid.delete')}
              </button>
            </div>
          </div>
        )}
      </div>

      {confirmingDelete && deal && (
        <ConfirmDialog destructive
          message={deal.tasks.length > 0
            ? t('pipelineGrid.deleteConfirmWithTasks', { count: deal.tasks.length, name: deal.name })
            : t('pipelineGrid.deleteConfirm', { name: deal.name })}
          onConfirm={() => void handleDelete()}
          onClose={() => setConfirmingDelete(false)} />
      )}
      {editingPicker?.field === 'stage' && deal && stagesFetch.status !== 'error' && (
        <ValueSheet shape={sheetShape} title={`${t('pipelineGrid.columns.stage')} — ${deal.name}`} options={stageOptions} current={deal.stageId}
          anchor={editingPicker.anchor} onSelect={(v) => { setEditingPicker(null); if (v) void applyStage(v); }} onClose={() => setEditingPicker(null)} />
      )}
      {editingPicker?.field === 'stage' && deal && stagesFetch.status === 'error' && (
        <SheetShell shape={sheetShape} anchor={editingPicker.anchor} onClose={() => setEditingPicker(null)} className={sheetShape === 'popover' ? 'w-64' : ''}>
          <div data-testid="stage-picker-error" className="p-4 flex flex-col items-center gap-3 text-center">
            <div className="text-sm text-text-muted">{t('listStates.error')}</div>
            <button type="button" data-testid="stage-picker-retry" onClick={stagesFetch.retry}
              className="min-h-[44px] px-4 rounded-lg border border-border-neutral text-sm font-semibold text-text-strong hover:bg-canvas">
              {t('listStates.retry')}
            </button>
          </div>
        </SheetShell>
      )}
      {editingPicker?.field === 'owner' && deal && (
        <ValueSheet shape={sheetShape} title={`${t('pipelineGrid.columns.owner')} — ${deal.name}`} options={memberOptions} current={deal.ownerId}
          anchor={editingPicker.anchor} onSelect={(v) => { setEditingPicker(null); void patch({ ownerId: v }); }} onClose={() => setEditingPicker(null)} />
      )}
      {feedback.node}
    </div>
  );
};
